import { buildCombinationKey, cartesianProduct, Locales } from "@simplyk/common";

import { ShopVariantData, VariantSeatData } from "../Rates/types";

import { RateOutput } from "@/common/types/trpc";

type PreviewShopVariants = NonNullable<RateOutput["shopVariants"]>;
type PreviewChildRatesInfo = NonNullable<RateOutput["childRatesInfo"]>;

interface ExpandArgs {
  rateId: string;
  shopVariants: ShopVariantData[] | undefined;
  variantSeats: VariantSeatData[] | undefined;
  locale: Locales;
}

/**
 * Transforms editor-shape `shopVariants` (flat `name`) into the output shape
 * the public form preview expects (`shopVariantFields` / `shopVariantValueFields`)
 * and derives `childRatesInfo` via the cartesian product of values.
 *
 * Child rate ids are deterministic (rateId + value ids) so that preview selections
 * stay stable as the user tweaks the editor.
 */
export function buildPreviewShopVariantsAndChildren({ rateId, shopVariants, variantSeats, locale }: ExpandArgs): {
  shopVariants: PreviewShopVariants;
  childRatesInfo: PreviewChildRatesInfo;
} {
  const activeVariants = (shopVariants ?? []).filter((v) => v.values.length > 0);

  if (activeVariants.length === 0) {
    return { shopVariants: [], childRatesInfo: [] };
  }

  const previewShopVariants: PreviewShopVariants = activeVariants.map((variant) => ({
    id: variant.id,
    sortIndex: variant.sortIndex,
    type: variant.type,
    isArchived: false,
    shopVariantFields: [
      {
        id: `${variant.id}-preview-field`,
        locale,
        name: variant.name,
      },
    ],
    shopVariantValues: variant.values.map((value) => ({
      id: value.id,
      sortIndex: value.sortIndex,
      isArchived: false,
      shopVariantValueFields: [
        {
          id: `${value.id}-preview-field`,
          locale,
          name: value.name,
        },
      ],
    })),
  }));

  const seatsByCombinationKey = new Map<string, number | null>();
  for (const entry of variantSeats ?? []) {
    seatsByCombinationKey.set(entry.combinationKey, entry.seats);
  }

  const valueIdsPerVariant = activeVariants.map((variant) => variant.values.map((value) => value.id));
  const combinations = cartesianProduct(valueIdsPerVariant);

  const childRatesInfo: PreviewChildRatesInfo = combinations.map((variantValueIds) => {
    const combinationKey = buildCombinationKey(variantValueIds);
    return {
      childRateId: `${rateId}-preview-${combinationKey}`,
      variantValueIds,
      seats: seatsByCombinationKey.get(combinationKey) ?? null,
    };
  });

  return { shopVariants: previewShopVariants, childRatesInfo };
}
